import React, { forwardRef, useEffect, useRef } from 'react'

import { Avatar, Button } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import i18n from 'i18n'
import classNames from 'classnames'
import moment from 'moment'

import FoldableFlow from 'emailThreadViewer/FoldableFlow'
import { getImageInfo } from 'components/FTIcon/getImageInfo'
import calender2SVG from 'assets/images/icon-calendar2.svg'

import { applyPseudoStyleScope } from 'libs/style-to-inline'
import { getSafeHTML, scrollIntoViewIfNeeded } from 'utils'
import { linkifyMailNode } from '../helper/linkifyMail'
import { handleSchemaEventClick } from '../helper/mailview'
import { formatBytes, getInitialAvatarSrc } from 'utils/ui'
import { getUserEmail } from 'emailThreadViewer/helper/utils'
import { getDisplayEmailElem } from 'emailThreadViewer/helper/uiHelpers'
import { getDisplayEmailHTMLStr } from 'emailThreadViewer/helper'

import printLogoSVG from 'assets/images/logo_no_icon.svg'

import exclamation24 from 'assets/images/exclamation-circle-24.svg'

import BlockedBanner from 'utils/ui/SecureSend/BlockedBanner'
import ConfidentialBanner from 'utils/ui/SecureSend/ConfidentialBanner'

import Attachment from '../Attachment'

import IFace from '../ViewerIFace'
import type { ReadReceiptInfo, ReadReceiptStatus } from '../ViewerIFace'

import ReadReceiptChip from './ReadReceiptChip'

import uiExt from './uiExt'

import styles from './style.module.scss'
import {
  TextFindableNode,
  useTextFinderContext,
  type FocusInfo,
} from 'emailThreadViewer/TextFinder'
import { composeRefs } from 'emailThreadViewer/TextFinder/utils/composeRefs'
import Truncate from './Truncate'

import * as planer from 'libs/planer/planer'

// OMD-28: 본문(.htmlMailBody)의 실제 content box 폭을 getSafeHTML width 인자로 넘길 때 쓰는 보정값.
// 폭 소스는 항상 마운트되어 있는 `.container`(containerRef)이며, 본문이 렌더되는 `.viewContainer`는
// getSafeHTML 결과(state)로만 렌더되어 호출 시점엔 존재하지 않으므로 상수로 보정한다.
// 88 = `.viewContainer` padding(0 32px 16px 56px)의 좌 56 + 우 32. `.container`는 overflow:hidden 이라
// clientWidth = offsetWidth − border 이고, 중간 `.mailBody` 래퍼는 좌우 padding/border 가 없다.
const VIEW_CONTAINER_HORIZONTAL_PADDING = 88

interface EmailAddress {
  name: string
  email: string
}

interface MailInfo {
  id: string
  messageId?: string
  draft?: boolean
  sender?: EmailAddress
  from: EmailAddress
  to?: EmailAddress[]
  cc?: EmailAddress[]
  bcc?: EmailAddress[]
  subject?: string
  summary?: string
  read?: boolean
  body?: BodyData
  received_time: number
  quoted_visible?: boolean
  has_attachment?: boolean
  enable_flag?: boolean
  priority?: string
  sensitivity?: string
  flagged?: boolean
  priority_inbox?: string
  is_scheduled_email?: boolean
  ai_summary?: string
  enable_reply_forward?: boolean
  enable_read?: boolean
  enable_delete?: boolean
  enable_print?: boolean
  is_text_partial?: boolean
}

interface InlineImage {
  cid: string
  url: string
  id?: string
}

interface AttachmentObj {
  id: string
  name?: string
  size: number
  mime_type: string
  enable_preview?: boolean
  enable_open?: boolean
  enable_save_as?: boolean
}

interface BodyData {
  type: string
  body: string
  attachments?: AttachmentObj[]
  inline_images?: InlineImage[]
  meeting_info?: MeetingInfo
}

interface MeetingInfo {
  dt_start: number
  dt_end: number
  type: number
  timezone?: string
  location?: string
  sender: EmailAddress
  title: string
  new_start_propose_time?: number
  new_end_propose_time?: number
  propose_comment?: string
  enable_response?: boolean
  meeting_response_actions?: number[]
  meeting_proposal_accepted?: boolean
  recurrence?: string
  sequence?: number
}

interface ReactMgr {
  addReactObj: (obj: any) => void
  removeReactObj: (obj: any) => void
}

// EmailView 내부에서 host window 의 docUtil 글로벌 (mailview.ts startMailView 가 등록)
// 에 접근하기 위한 inline narrowing type. mailview.ts 의 MailViewWindow 와 동일 source
// 이지만 viewer 패키지의 다른 영역과 type 충돌을 회피하기 위해 local 정의.
type WindowWithDocUtil = Window & {
  docUtil?: {
    addViewElement: (id: string, elem: HTMLElement | undefined) => void
    mailViewMap: Record<
      string,
      {
        notifyVisible: () => void
        checkOption: (opts: { mode: string }) => void
      }
    >
  }
}

// CSS @page 는 비표준 CSSStyleDeclaration property (print 영역). element.style.page
// 직접 할당 패턴을 좁힘.
type StyleWithPage = CSSStyleDeclaration & { page?: string }

interface ThreadItem {
  mailInfo: MailInfo
  reactMgr: ReactMgr
  reactObj?: EmailView
  reqBody: () => void
  setRead: (read: boolean) => void
  setFlagged: (flagged: boolean) => void
}

interface EmailViewProps {
  threadItem: ThreadItem
  fold?: boolean
  hidden?: boolean
  selected?: boolean
  isFirst?: boolean
  hasTitle?: boolean
  owner?: any
  bodySearch?: boolean
}

interface EmailViewState {
  hasExternalImg: boolean | null
  isBlockedRes: boolean | null
  infoElem: React.ReactNode | null
  meetingInfo: MeetingInfo | null
  bodyElem: React.ReactNode | null
  fullBodyElem: React.ReactNode | null
  isDraft: boolean
  foldAttachment: boolean
  attachmentMinCnt: number
  allFileSize: number
  isExpandEnabled: boolean
  isFold: boolean | null
  isQuotedVisible: boolean
  hasQuoted: boolean | null
  actionsWidth: number | null
  isLess400: boolean | null
  avatar?: string
  attachments?: React.ReactNode[]
  isSecureMail: boolean
  secureSendExpired: string | null
  secureMailId: string | null
  isReadReceipt: boolean
  readReceiptStatus: ReadReceiptStatus | null
  receiptId: string | null
}

interface ObserverObj {
  element?: HTMLElement
  ref?: HTMLElement
  onClick?: (e: MouseEvent) => void
  onDblClick?: (e: MouseEvent) => void
  clear: () => void
}

interface AttachmentsObserver {
  element?: HTMLElement
  ref?: HTMLElement
  resizeTimer?: number
  calcHeight: () => void
  observer?: ResizeObserver
  clear: () => void
}

interface ContainerObserver {
  element?: HTMLElement
  ref?: HTMLElement
  resizeTimer?: number
  onResize: () => void
  observer?: ResizeObserver
  clear: () => void
}

export class EmailView extends React.Component<EmailViewProps, EmailViewState> {
  isBlockedRes: boolean | null = null
  isAllowedSender = false
  inlineImgList: InlineImage[] = []
  existInlineImgMap: Record<string, InlineImage> = {}
  isReqBody = false
  isQuotedVisible = false
  isUnmounted = false
  checkSenderTimeoutId: ReturnType<typeof setTimeout> | null = null
  secureSendExpireTimerId: ReturnType<typeof setTimeout> | null = null
  isExpandEnabled = false
  divFrameMap: Record<string, HTMLElement> = {}
  attachmentProgressMap: Record<string, (err: any, progress: any) => void> = {}
  observerObj?: ObserverObj
  attachmentsObserver?: AttachmentsObserver
  containerObserver?: ContainerObserver
  containerRef?: HTMLElement
  prevLess400?: boolean
  prevRefWidth?: number
  loading?: boolean
  printWindow: Window | null = null

  constructor(props: EmailViewProps) {
    super(props)

    this.state = {
      hasExternalImg: null,
      isBlockedRes: null,
      infoElem: null,
      meetingInfo: null,
      bodyElem: null,
      fullBodyElem: null,
      isDraft: false,
      foldAttachment: true,
      attachmentMinCnt: 0,
      allFileSize: 0,
      isExpandEnabled: false,
      isFold: null,
      isQuotedVisible: false,
      hasQuoted: null,
      actionsWidth: null,
      isLess400: null,
      isSecureMail: false,
      secureSendExpired: null,
      secureMailId: null,
      isReadReceipt: false,
      readReceiptStatus: null,
      receiptId: null,
    }
  }

  componentDidMount() {
    const { threadItem } = this.props
    const { mailInfo, reactMgr } = threadItem

    i18n.on('languageChanged', this.onLanguageChanged)

    reactMgr.addReactObj(this)

    this.initData()

    if (threadItem != null) {
      // details 요청 후 받은 데이터를 처리하기 위해 필요.
      threadItem.reactObj = this

      if (mailInfo.draft === true) {
        this.setResourceBlocked(false)

        this.setResInfo({
          email: mailInfo.sender != null ? mailInfo.sender.email : '',
        })

        this.reqDetails()
      } else {
        this.checkAllowedAndKnownSender()
      }
    }
  }

  componentDidUpdate(prevProps: EmailViewProps) {
    const { fold, hidden } = this.props

    if (prevProps != null && (prevProps.fold !== fold || prevProps.hidden !== hidden)) {
      if (prevProps.fold !== fold) {
      }
      const { isFold } = this.state
      if (isFold != null) {
        this.setState({
          isFold: null,
        })
      } else {
        this.forceUpdate()
      }
    }
  }

  componentWillUnmount() {
    const { threadItem } = this.props
    const { reactMgr } = threadItem

    this.isUnmounted = true
    if (this.checkSenderTimeoutId != null) {
      clearTimeout(this.checkSenderTimeoutId)
      this.checkSenderTimeoutId = null
    }
    if (this.secureSendExpireTimerId != null) {
      clearTimeout(this.secureSendExpireTimerId)
      this.secureSendExpireTimerId = null
    }

    reactMgr.removeReactObj(this)

    if (this.printWindow != null && !this.printWindow.closed) {
      this.printWindow.close()
      this.printWindow = null
    }

    i18n.off('languageChanged', this.onLanguageChanged)
    if (this.observerObj != null) {
      this.observerObj.clear()
    }

    if (this.attachmentsObserver != null) {
      this.attachmentsObserver.clear()
    }

    if (this.containerObserver != null) {
      this.containerObserver.clear()
      this.containerObserver.element = undefined
      this.containerObserver.ref = undefined
    }

    // threadItem(EmailData)이 외부(window.singleUse 콜백 등)에서 살아남아도
    // unmount된 인스턴스와 detached 본문 div가 함께 유지되지 않도록 참조를 끊는다.
    if (threadItem != null && threadItem.reactObj === this) {
      threadItem.reactObj = undefined
    }
    this.divFrameMap = {}
    this.attachmentProgressMap = {}
    this.inlineImgList = []
    this.existInlineImgMap = {}
    this.containerRef = undefined
  }

  reset = () => {
    this.isReqBody = false
  }

  initData = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { quoted_visible: quotedVisible } = mailInfo

    this.isQuotedVisible = quotedVisible === true

    this.divFrameMap = {}

    this.applySender(true)
  }

  applySender = (_init?: boolean) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { from } = mailInfo

    const sender = from

    if (sender != null && sender.email != null && sender.email.trim().length > 0) {
      const avatar = getInitialAvatarSrc(sender.name, sender.email)

      this.setState({
        avatar,
      })

      IFace.WebToNative.getAvatar(sender.email, sender.name).then((ret: any) => {
        const { url, type, data } = ret
        if (url != null) {
          this.setState({
            avatar: url,
          })
        } else if (data != null && type != null) {
          const dataUrl = `data:${type};base64,${data}`
          this.setState({
            avatar: dataUrl,
          })
        }
      })
    }
  }

  onDraftChanged = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    this.setResInfo({
      email: mailInfo.sender != null ? mailInfo.sender.email : '',
    })
  }

  reqDetails = (forced?: boolean) => {
    const { fold, threadItem } = this.props

    if (this.isReqBody !== true && (fold === false || forced === true)) {
      this.isReqBody = true

      this.loading = true

      setTimeout(() => {
        requestAnimationFrame(() => {
          threadItem.reqBody()
        })
      }, 20)
    }
  }

  isFold = (): boolean => {
    const { fold } = this.props
    const { isFold } = this.state
    let ret: boolean
    if (isFold == null) {
      ret = fold != null ? fold : false
    } else {
      ret = isFold
    }

    if (ret === false) {
      this.reqDetails(true)
    }

    return ret
  }

  setFold = (fold: boolean, notifyOwner?: boolean) => {
    const { isFold } = this.state

    // 변경 1a — 펼침 부수효과 멱등화: 이미 펼쳐진 상태(isFold === false)에서 재호출되면
    // 펼침 부수효과(notifyVisible 등)를 반복하지 않는다. notifyVisible → notifyResized →
    // 리렌더 → 재포커스로 이어지는 무한 루프(고리 A)를 차단한다.
    // 판단은 state.isFold로만 한다. isFold()는 props.fold 폴백 때문에 최초 펼침을 오판한다.
    if (fold === false && isFold !== false) {
      const { threadItem } = this.props
      const { mailInfo } = threadItem

      const { read, id } = mailInfo

      if (read !== true) {
        threadItem.setRead(true)
      }

      if (
        (window as WindowWithDocUtil).docUtil != null &&
        (window as WindowWithDocUtil).docUtil.mailViewMap[id] != null
      ) {
        ;(window as WindowWithDocUtil).docUtil.mailViewMap[id].notifyVisible()
      }
    }

    if (notifyOwner !== false && isFold !== fold) {
      const { owner, threadItem } = this.props
      owner.notifyEmailFoldChanged(threadItem, fold)
    }

    // 변경 1b — setState 멱등화: 동일 값(false→false) setState도 비 PureComponent 클래스에서는
    // 리렌더를 유발하고, 인라인 onFocusChange와 결합 시 리렌더 루프(고리 B)의 트리거가 된다.
    // (React의 same-value bailout은 useState 훅 전용으로 this.setState에는 적용되지 않는다)
    if (isFold !== fold) {
      this.setState({
        isFold: fold,
      })
    }
  }

  onClickContainer = () => {
    const { threadItem, owner } = this.props
    const { mailInfo } = threadItem

    if (owner != null && owner.setSelectedInner) {
      owner.setSelectedInner(mailInfo.id)
    }

    // native 인터페이스(WebToNative) 초기화(initIFace) 전 호출 시 함수 부재 (OFFICEMAIL-WEB-ACCESS-M7)
    if (typeof IFace.WebToNative?.notifySelect === 'function') {
      IFace.WebToNative.notifySelect(mailInfo.id)
    }
  }

  onClickHeader = (e: React.MouseEvent) => {
    const emailElem = (e.target as HTMLElement).closest('.emailSet[data-email]')
    if (emailElem != null) {
      const email = emailElem.getAttribute('data-email')
      const name = emailElem.getAttribute('data-name')
      IFace.WebToNative.showContact(email, name)

      e.preventDefault()
      e.stopPropagation()
      return
    }

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && selection.getRangeAt(0).collapsed !== true) {
      return
    }

    const newFold = !this.isFold()
    this.setFold(newFold)
  }

  onClickAvatar = (e: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem
    const { from } = mailInfo
    const { email, name } = from

    IFace.WebToNative.showContact(email, name)

    e.preventDefault()
    e.stopPropagation()
  }

  checkAllowedAndKnownSender = (retryCount: number = 0) => {
    if (this.isUnmounted) return

    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { from, messageId } = mailInfo

    const sender = from

    if (sender == null || sender.email == null) {
      this.setResourceBlocked(true)
      this.reqDetails()
      return
    }

    const TIMEOUT_MS = 5000
    let settled = false

    if (this.checkSenderTimeoutId != null) {
      clearTimeout(this.checkSenderTimeoutId)
    }

    this.checkSenderTimeoutId = setTimeout(() => {
      this.checkSenderTimeoutId = null
      if (settled || this.isUnmounted) return
      settled = true

      if (retryCount < 2) {
        this.checkAllowedAndKnownSender(retryCount + 1)
      } else {
        console.warn('checkAllowedAndKnownSender timeout after retries')
        this.setResourceBlocked(true)
        this.reqDetails()
      }
    }, TIMEOUT_MS)

    IFace.WebToNative.checkAllowedAndKnownSender(messageId, sender.email)
      .then((res: { isBlocked: boolean; isAllowedSender: boolean | null }) => {
        if (settled || this.isUnmounted) return
        settled = true
        if (this.checkSenderTimeoutId != null) {
          clearTimeout(this.checkSenderTimeoutId)
          this.checkSenderTimeoutId = null
        }

        const { isBlocked, isAllowedSender } = res

        if (isAllowedSender != null) {
          this.isAllowedSender = isAllowedSender
        }

        if (isBlocked === false) {
          this.setResourceBlocked(false)
        } else {
          this.setResourceBlocked(true)
          if (isAllowedSender === false) {
            this.setResInfo({
              email: sender.email,
            })
          }
        }

        this.reqDetails()
      })
      .catch(() => {
        if (settled || this.isUnmounted) return
        settled = true
        if (this.checkSenderTimeoutId != null) {
          clearTimeout(this.checkSenderTimeoutId)
          this.checkSenderTimeoutId = null
        }

        if (retryCount < 2) {
          this.checkAllowedAndKnownSender(retryCount + 1)
        } else {
          console.warn('checkAllowedAndKnownSender error after retries')
          this.setResourceBlocked(true)
          this.reqDetails()
        }
      })
  }

  setResourceBlocked = (isBlocked: boolean) => {
    if (this.isBlockedRes !== isBlocked) {
      this.isBlockedRes = isBlocked
      if (isBlocked === false) {
        Object.values(this.divFrameMap).forEach((divElem) => {
          this.applyBlockedImg(divElem)
        })
      }
      this.setState({
        isBlockedRes: this.isBlockedRes,
      })
    }
  }

  onLanguageChanged = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    this.setResInfo({ email: mailInfo.from.email })
  }

  setResInfo = (info: { email: string }) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    let infoElem: React.ReactNode = null
    if (mailInfo.draft !== true) {
      infoElem = (
        <div className={classNames(styles.resInfo)}>
          <div className={classNames(styles.icon)}>
            <img src={exclamation24} />
          </div>

          <div className={classNames(styles.content)}>
            <div className={classNames(styles.desc1)}>{i18n.t('email.desc.safeSender')}</div>
            <div className={classNames(styles.desc2)}>
              {this.isAllowedSender !== true && (
                <a href="" onClick={(e) => this.addTrustEmail(e, info.email)}>
                  {i18n.t('email.desc.trustSender', { email: info.email })}
                </a>
              )}

              <a
                href=""
                onClick={(e) => {
                  this.unblockRes(e)
                }}
              >
                {i18n.t('email.desc.showBlocked')}
              </a>
            </div>
          </div>
        </div>
      )
    }

    this.setState({ infoElem, isDraft: mailInfo.draft === true })
  }

  addTrustEmail = (e: React.MouseEvent, email: string) => {
    IFace.WebToNative.addAllowedSender(email).then(() => {
      this.setResourceBlocked(false)
    })

    e.preventDefault()
    e.stopPropagation()
  }

  unblockRes = (e: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    IFace.WebToNative.addTempAllowedEmailId(mailInfo.id)

    this.setResourceBlocked(false)

    e.preventDefault()
    e.stopPropagation()
  }

  onSetDetails = (bodyData: BodyData) => {
    const { threadItem, isFirst } = this.props
    const { mailInfo } = threadItem

    let bodyElem: React.ReactNode = null

    const {
      type,
      body,
      attachments,
      inline_images: inlineImages,
      meeting_info: meetingInfo,
    } = bodyData

    if (inlineImages != null) {
      this.onSetInlineImages(inlineImages)
    }

    let newState: Partial<EmailViewState> = {}

    if (attachments != null) {
      let allFileSize = 0

      const attachmentElems = attachments.map((attachmentObj) => {
        const attrs: any = {
          attachmentObj,
          key: `${attachmentObj.id}`,
          downloadable: true,
          enableDelete: false,
          api: {
            setProgressCB: (onProgressCB: (err: any, progress: any) => void) => {
              this.attachmentProgressMap[attachmentObj.id] = onProgressCB
            },
            download: (onDownload: (ret: any) => void) => {
              IFace.WebToNative.download(mailInfo.id, attachmentObj.id).then((ret: any) => {
                console.log('download - ', ret)
                if (onDownload != null) {
                  onDownload(ret)
                }
              })
            },
            cancelDownload: () => {
              IFace.WebToNative.cancelDownload(mailInfo.id, attachmentObj.id)
            },
            downloadFile: () => {
              IFace.WebToNative.downloadFile(mailInfo.id, attachmentObj.id)
            },
            showPreview: () => {
              IFace.WebToNative.showPreview(mailInfo.id, attachmentObj.id)
            },
            open: () => {
              IFace.WebToNative.open(mailInfo.id, attachmentObj.id)
            },
            saveAs: () => {
              IFace.WebToNative.saveAs(mailInfo.id, attachmentObj.id)
            },
            saveAndOpen: () => IFace.WebToNative.saveAndOpen(mailInfo.id, attachmentObj.id),
            showInFolder: () => IFace.WebToNative.showInFolder(mailInfo.id, attachmentObj.id),
          },
        }

        const attachmentElem = <Attachment {...attrs} />
        allFileSize += attachmentObj.size

        return attachmentElem
      })

      newState = {
        ...newState,
        attachments: attachmentElems,
        allFileSize,
      }
    }

    if (meetingInfo != null) {
      newState = {
        ...newState,
        isBlockedRes: false,
        meetingInfo,
      }
    } else {
      newState = {
        ...newState,
        meetingInfo: null,
      }
    }

    if (type === 'text/html') {
      this.applyBodyElem(body, inlineImages)
    } else {
      if (type === 'text/plain') {
        let bodyText = body
        let fullBodyElem: React.ReactNode = null
        if (this.isQuotedVisible === false && isFirst !== true) {
          const fullBodyText = bodyText
          const prevText = bodyText
          bodyText = planer.extractFrom(bodyText, 'text/plain', window.document)
          if (prevText !== bodyText) {
            newState = {
              ...newState,
              hasQuoted: true,
            }
          }
          fullBodyElem = <pre className={styles.plainBody}>{fullBodyText}</pre>
        }
        bodyElem = <pre className={styles.plainBody}>{bodyText}</pre>
        newState = {
          ...newState,
          bodyElem,
          fullBodyElem,
          isBlockedRes: false,
        }
      } else if (type === 'text/calendar') {
        this.applyBodyElem(body, inlineImages)

        newState = {
          ...newState,
          isBlockedRes: false,
        }
      }

      this.loading = false
    }

    if (Object.keys(newState).length > 0) {
      this.setState(newState as EmailViewState)
    }
  }

  showQuoted = (e?: React.MouseEvent) => {
    this.isQuotedVisible = true

    this.setState({
      isQuotedVisible: this.isQuotedVisible,
    })

    const { threadItem } = this.props
    const { mailInfo } = threadItem
    if (this.divFrameMap.fullBody != null) {
      ;(window as WindowWithDocUtil).docUtil.addViewElement(mailInfo.id, this.divFrameMap.fullBody)
    } else if (this.divFrameMap.body != null) {
      ;(window as WindowWithDocUtil).docUtil.addViewElement(mailInfo.id, this.divFrameMap.body)
    }

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  onSetInlineImages = (inlineImages: InlineImage[]) => {
    if (inlineImages != null && Array.isArray(inlineImages) && inlineImages.length > 0) {
      this.inlineImgList = [...this.inlineImgList, ...inlineImages]

      inlineImages.forEach((inlineData) => {
        const { cid, url } = inlineData
        this.existInlineImgMap[cid] = inlineData
        Object.values(this.divFrameMap).forEach((divElem) => {
          this.applyInlineImg(divElem, cid, url)
        })
      })
    }
  }

  onSetSecureSendInfo = (data: {
    isSecureMail: boolean
    expiredDate: string | null
    secureMailId: string | null
  }): void => {
    this.setState({
      isSecureMail: data.isSecureMail,
      secureSendExpired: data.expiredDate,
      secureMailId: data.secureMailId,
    })

    if (this.secureSendExpireTimerId != null) {
      clearTimeout(this.secureSendExpireTimerId)
      this.secureSendExpireTimerId = null
    }

    if (data.isSecureMail && data.expiredDate != null) {
      this.scheduleSecureSendExpireCheck(data.expiredDate)
    }
  }

  onSetReadReceiptInfo = (isReadReceipt: boolean, data: ReadReceiptInfo | null): void => {
    this.setState({
      isReadReceipt,
      readReceiptStatus: data != null ? data.status : null,
      receiptId: data != null ? data.receiptId : null,
    })
  }

  openReadReceiptDetail = (): void => {
    if (this.state.readReceiptStatus == null) return
    // 5차 보완: viewer iframe 마스크 클립 회피를 위해 host 컨텍스트에서 popup 마운트.
    // host 가 emailId 로 emailInfoMap.readReceiptInfo.status 를 lookup 후 popup 을 띄움.
    const emailId = this.props.threadItem?.mailInfo?.id
    if (emailId == null || emailId.length === 0) return
    // 칩 클릭 시 refresh 도 함께 트리거 — host 가 캐시 삭제 후 applyReadReceiptInfo 재호출.
    // host 의 dispatchReadReceiptInfoToViewer 가 readReceiptListenerStore.notify 를 부르며,
    // 동시에 띄워진 popup 의 Content 가 동일 emailId 를 구독하므로 자동 갱신된다.
    IFace.WebToNative.requestRefreshReadReceiptInfo?.(emailId)
    IFace.WebToNative.openReadReceiptDetailPopup(emailId)
  }

  refreshReadReceipt = (emailId: string): void => {
    if (emailId == null || emailId.length === 0) return
    // ViewerIFace WebToNative 채널 — 호스트(apps/owma) 측에서 캐시 삭제 후 applyReadReceiptInfo 재호출
    IFace.WebToNative.requestRefreshReadReceiptInfo?.(emailId)
  }

  scheduleSecureSendExpireCheck = (expiredDate: string): void => {
    const THIRTY_MINUTES = 30 * 60 * 1000
    const expireTime = new Date(expiredDate).getTime()
    const remaining = expireTime - Date.now()

    if (remaining <= 0) {
      this.forceUpdate()
      return
    }

    const delay = remaining > THIRTY_MINUTES ? THIRTY_MINUTES : remaining

    this.secureSendExpireTimerId = setTimeout(() => {
      this.secureSendExpireTimerId = null
      if (this.isUnmounted) return

      if (Date.now() >= expireTime) {
        this.forceUpdate()
      } else {
        this.scheduleSecureSendExpireCheck(expiredDate)
      }
    }, delay)
  }

  notifyDownloadProgress = (attachmentId: string, progress: any) => {
    const onProgressCB = this.attachmentProgressMap[attachmentId]
    if (onProgressCB != null) {
      onProgressCB(null, progress)
    }
  }

  applyInlineImgList = (divElem: HTMLElement) => {
    this.inlineImgList.forEach((inlineData) => {
      const { cid, url } = inlineData
      this.existInlineImgMap[cid] = inlineData
      this.applyInlineImg(divElem, cid, url)
    })
  }

  // OMD-28: getSafeHTML 에 넘길 본문 content box 폭(px 문자열). 호출 시점에 항상 마운트되어 있는
  // `.container`(containerRef) 기준으로 계산한다. 폭이 유효하지 않으면 undefined 를 반환해 getSafeHTML
  // 이 기본값(720px)으로 폴백하도록 한다.
  getBodyWidthArg = (): string | undefined => {
    const container = this.containerRef
    const contentWidth =
      container != null ? container.clientWidth - VIEW_CONTAINER_HORIZONTAL_PADDING : 0
    const widthArg = contentWidth > 0 ? `${contentWidth}px` : undefined

    // 폭이 0/미마운트라 720 으로 조용히 폴백되면 diff 검증(720==720)이 이를 못 잡으므로, dev 에서만
    // 폴백/전달 폭을 남겨 폴백 발생을 가시화한다(프로덕션 번들에서는 dead code 로 제거).
    if (!__ENV_PRODUCTION__) {
      if (widthArg == null) {
        console.warn('[OMD-28] getSafeHTML width fallback → 720px', {
          hasContainer: container != null,
          clientWidth: container?.clientWidth ?? null,
        })
      } else {
        console.debug('[OMD-28] getSafeHTML width', widthArg)
      }
    }

    return widthArg
  }

  applyBodyElem = (body: string, inlineImages?: InlineImage[]) => {
    const { isFirst } = this.props
    // OMD-28: 실제 컨테이너 폭을 getSafeHTML width 로 넘겨 media query/%/auto 레이아웃이 실제 표시
    // 폭 기준으로 인라인되도록 한다. fullBody/body 두 호출 모두 같은 `.viewContainer` 안에 렌더되므로
    // 동일 폭을 적용한다.
    const bodyWidth = this.getBodyWidthArg()
    const inlineImageMap: Record<string, InlineImage> = {}
    if (inlineImages != null) {
      inlineImages.forEach((imageFile) => {
        inlineImageMap[imageFile.cid] = imageFile
      })
    }

    let bodyHTML = body
    let fullBodyHTML: string | null = null
    // OFM-221: planer 가 실행되면 bodyHTML 이 문자열 단계에서 별도 변형되어 doc 와 불일치하므로,
    // planer 미실행 시에만 파싱된 doc 를 getSafeHTML 에 직접 전달해 재파싱을 생략한다.
    let plannerRan = false

    let hasExternalImg = false

    const domParser = new DOMParser()
    const doc = domParser.parseFromString(bodyHTML, 'text/html')

    const imgList = doc.querySelectorAll('img')

    imgList.forEach((img) => {
      if (img.src.toLowerCase().indexOf('resources:') !== 0) {
        if (img.src.toLowerCase().indexOf('cid:') === 0) {
          const cid = img.src.substring(4)
          const inlineData = inlineImageMap[cid]
          if (inlineData != null) {
            img.src = inlineData.url
          } else {
            img.setAttribute('owa-cid', img.src)
            img.src = ''
          }
        } else if (this.isBlockedRes !== false) {
          hasExternalImg = true
          img.setAttribute('owa-src', img.src)
          const srcset = img.getAttribute('srcset')
          img.src = ''
          if (srcset != null) {
            img.setAttribute('owa-srcset', srcset)
            img.setAttribute('srcset', '')
          }
        }
      }
    })

    if (this.isQuotedVisible === false && isFirst !== true) {
      fullBodyHTML = doc.documentElement.outerHTML

      const quotedHeader = doc.querySelector(
        '[id^="quoted_header_"].quoted_header_editor, div[style="border:none;border-top:solid #B5C4DF 1.0pt;padding:3.0pt 0cm 0cm 0cm"]',
      )
      if (quotedHeader != null) {
        let tmpElem: Element | null = quotedHeader
        while (tmpElem != null) {
          const next =
            tmpElem.nextSibling != null ? tmpElem.nextSibling : tmpElem.parentElement?.nextSibling
          tmpElem.parentElement?.removeChild(tmpElem)
          tmpElem = next as Element | null
        }

        this.setState({
          hasQuoted: true,
        })
      }
      bodyHTML = doc.documentElement.outerHTML

      bodyHTML = bodyHTML.replace('\r\n', '\n')
      // quotedHeader 시그니처가 이미 매칭되어 인용이 제거된 경우 planer 호출 스킵.
      // 큰 메일일수록 planer 비용이 크므로 중복 실행을 피한다.
      if (quotedHeader != null) {
      } else {
        plannerRan = true
        const prevHTML = bodyHTML
        bodyHTML = planer.extractFrom(bodyHTML, 'text/html', window.document)

        if (prevHTML !== bodyHTML) {
          this.setState({
            hasQuoted: true,
          })
        }
      }
    } else {
      bodyHTML = doc.documentElement.outerHTML
    }

    if (hasExternalImg === false) {
      this.setState({
        hasExternalImg: false,
      })
    } else {
      this.setResourceBlocked(this.isBlockedRes!)
    }

    // 툴바 인용구(variant) 의사요소 장식을 뷰어에 보존하기 위한 메일별 scope id.
    // getSafeHTML 은 이 값이 있으면 의사요소 <style data-ngw-pseudo> 를 unscoped 로 주입하고,
    // setDivBody 가 `@scope ([data-email-id="{id}"] .htmlMailBody)` 로 감싸 메일 본문에 격리한다.
    const pseudoScopeId = this.props.threadItem?.mailInfo?.id

    if (this.isQuotedVisible === false && fullBodyHTML != null) {
      getSafeHTML(
        fullBodyHTML,
        {
          pseudoScopeId,
          processElement: (element: HTMLElement) => {
            if (
              (element.style as StyleWithPage).page != null &&
              (element.style as StyleWithPage).page !== ''
            ) {
              ;(element.style as StyleWithPage).page = ''
            }
            // loading이 auto/lazy면 브라우저 장시간 실행·메모리 부족 시 이미지 로딩이 지연될 수 있음.
            if (element.tagName === 'IMG') {
              element.setAttribute('loading', 'eager')
            }
          },
        },
        bodyWidth,
      ).then((newHtml) => {
        const fullBodyElem = (
          <div
            className={classNames(styles.htmlMailBody, 'htmlMailBody')}
            ref={(ref) => {
              this.setDivBody(ref, true)
            }}
            dangerouslySetInnerHTML={{ __html: newHtml }}
          />
        )

        this.setState({
          fullBodyElem,
        })
      })
    }

    // OFM-221: planer 미실행 시 bodyHTML === doc.outerHTML 이므로, 재직렬화/재파싱 대신 doc 를 직접
    // 전달해 getSafeHTML 내부 DOMParser 를 생략한다. planer 실행 시엔 문자열이 변형되었으므로 폴백.
    getSafeHTML(
      plannerRan ? bodyHTML : doc,
      {
        pseudoScopeId,
        processElement: (element: HTMLElement) => {
          if (
            (element.style as StyleWithPage).page != null &&
            (element.style as StyleWithPage).page !== ''
          ) {
            ;(element.style as StyleWithPage).page = ''
          }
          // loading이 auto/lazy면 브라우저 장시간 실행·메모리 부족 시 이미지 로딩이 지연될 수 있음.
          if (element.tagName === 'IMG') {
            element.setAttribute('loading', 'eager')
          }
        },
      },
      bodyWidth,
    ).then((newHtml) => {
      const bodyElem = (
        <div
          className={classNames(styles.htmlMailBody, 'htmlMailBody')}
          ref={(ref) => {
            this.setDivBody(ref, false)
          }}
          dangerouslySetInnerHTML={{ __html: newHtml }}
        />
      )

      this.loading = false

      this.setState({
        bodyElem,
      })
    })
  }

  initRef = (ref: HTMLElement | null) => {
    if (this.observerObj == null) {
      this.observerObj = {} as ObserverObj

      this.observerObj.onClick = (e: MouseEvent) => {
        console.log(' - onClick Body : ', e)
        const linkElem = (e.target as HTMLElement).closest(`.${styles.htmlMailBody} a`)
        if (linkElem != null) {
          const url = (linkElem as HTMLAnchorElement).href

          const { threadItem } = this.props
          const { mailInfo } = threadItem

          IFace.WebToNative.openURL(mailInfo.id, url)

          e.preventDefault()
          e.stopPropagation()
          return
        }
      }

      this.observerObj.onDblClick = (e: MouseEvent) => {
        console.log(' - onDblClick Body : ', e)

        const imgElem = (e.target as HTMLElement).closest(`.${styles.htmlMailBody} img`)
        if (imgElem != null) {
          const aTag = imgElem.closest(`.${styles.htmlMailBody} a`)
          if (aTag == null) {
            // a tag에 포함되지 않은 img일 때.
            const { src } = imgElem as HTMLImageElement
            if (src != null && src.toLowerCase().indexOf('resources://images') === 0) {
              console.log('img src : ', src)
              IFace.WebToNative.showInlineImage(src)
            } else {
              const cidAttr = imgElem.getAttribute('owa-cid')
              if (cidAttr != null && cidAttr.indexOf('cid:') === 0) {
                const cid = cidAttr.substring(4)
                const cidInfo = this.inlineImgList.find((item) => item.cid === cid)
                if (cidInfo != null) {
                  console.log('img src : ', cidInfo.url)
                  IFace.WebToNative.showInlineImage(cidInfo.url)
                }
              }
            }
          }
        }
      }

      this.observerObj.clear = () => {
        const { element } = this.observerObj!
        if (element != null) {
          this.observerObj!.element!.removeEventListener('click', this.observerObj!.onClick!)
          this.observerObj!.element!.removeEventListener('dblclick', this.observerObj!.onDblClick!)
        }
      }
    }

    if (ref != null && this.observerObj.ref != ref) {
      this.observerObj.clear()

      this.observerObj.ref = ref
      this.observerObj.element = ref

      if (this.observerObj.element != null) {
        this.observerObj.element.addEventListener('click', this.observerObj.onClick!)
        this.observerObj.element.addEventListener('dblclick', this.observerObj.onDblClick!)
      }
    }
  }

  setDivBody = (ref: HTMLElement | null, isFullBody: boolean) => {
    if (ref != null) {
      if (isFullBody === true) {
        this.divFrameMap.fullBody = ref
      } else {
        this.divFrameMap.body = ref
      }

      if (this.isBlockedRes === false) {
        this.applyBlockedImg(ref)
      }

      this.applyInlineImgList(ref)

      // 의사요소 보존 <style>(getSafeHTML 이 unscoped 로 주입) 을 이 메일 본문으로만 한정.
      // @scope([data-email-id="{id}"] .htmlMailBody) 로 감싸 스레드 내 타 메일/앱 전역 오염을 막는다.
      const emailId = this.props.threadItem?.mailInfo?.id
      if (emailId != null && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        applyPseudoStyleScope(ref, `[data-email-id="${CSS.escape(emailId)}"] .htmlMailBody`)
      }

      if ((window as WindowWithDocUtil).docUtil != null) {
        const { threadItem } = this.props
        const { mailInfo } = threadItem
        if (this.isQuotedVisible === true && this.divFrameMap.fullBody != null) {
          ;(window as WindowWithDocUtil).docUtil.addViewElement(
            mailInfo.id,
            this.divFrameMap.fullBody,
          )
        } else if (this.isQuotedVisible === false && this.divFrameMap.body != null) {
          ;(window as WindowWithDocUtil).docUtil.addViewElement(mailInfo.id, this.divFrameMap.body)
        }
      }

      linkifyMailNode(ref)
    }
  }

  scrollIntoView = () => {
    if (this.containerRef != null) {
      console.log(' --- fold : ', this.props.fold, this.state.isFold)
      const { owner } = this.props
      owner.setScrollVisible(true)
      if (this.state.isFold !== false) {
        setTimeout(() => {
          scrollIntoViewIfNeeded(this.containerRef!.parentElement)
          owner.setScrollVisible(false)
        }, 250)
      } else {
        scrollIntoViewIfNeeded(this.containerRef!.parentElement)
        owner.setScrollVisible(false)
      }
    }
  }

  applyInlineImg = (divElem: HTMLElement, cid: string, url: string) => {
    if (divElem != null) {
      const imgs = divElem.querySelectorAll(`img[owa-cid="cid:${cid}"]`)
      imgs.forEach((img) => {
        ;(img as HTMLImageElement).src = url
      })
    }
  }

  applyBlockedImg = (divElem: HTMLElement) => {
    if (divElem != null) {
      const imgs = divElem.querySelectorAll(`img[owa-src]`)
      imgs.forEach((img) => {
        const htmlImg = img as HTMLImageElement
        htmlImg.src = img.getAttribute('owa-src')!
        const srcset = img.getAttribute('owa-srcset')
        if (srcset != null) {
          img.setAttribute('srcset', srcset)
          img.removeAttribute('owa-srcset')
        }
        img.removeAttribute('owa-src')
      })
    }
  }

  editDraft = (e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- editDraft : ', mailInfo.id)

    IFace.WebToNative.editDraft(mailInfo.id)

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  deleteDraft = (e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- deleteDraft : ', mailInfo.id)

    IFace.WebToNative.deleteDraft(mailInfo.id)

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  downloadAll = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- downloadAll : ', mailInfo.id)

    IFace.WebToNative.downloadAll(mailInfo.id)
  }

  setAttachmentRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      if (this.attachmentsObserver == null) {
        this.attachmentsObserver = {} as AttachmentsObserver

        this.attachmentsObserver.calcHeight = () => {
          if (this.attachmentsObserver!.resizeTimer != null) {
            cancelAnimationFrame(this.attachmentsObserver!.resizeTimer)
          }
          this.attachmentsObserver!.resizeTimer = requestAnimationFrame(() => {
            if (
              this.attachmentsObserver!.element != null &&
              this.attachmentsObserver!.element.children.length > 0
            ) {
              const top = this.attachmentsObserver!.element!.offsetTop
              const lastTop = (this.attachmentsObserver!.element!.lastElementChild as HTMLElement)!
                .offsetTop
              if (top < lastTop) {
                let idx = 0
                let item = this.attachmentsObserver!.element!.children[idx] as HTMLElement
                while (
                  item.offsetTop === top &&
                  idx < this.attachmentsObserver!.element!.children.length - 1
                ) {
                  idx += 1
                  item = this.attachmentsObserver!.element!.children[idx] as HTMLElement
                }

                const count = idx

                console.log(' --- view count : ', count)

                this.setState({
                  attachmentMinCnt: count,
                })
              } else {
                this.setState({
                  attachmentMinCnt: this.attachmentsObserver!.element!.children.length,
                })
              }
              this.attachmentsObserver!.resizeTimer = undefined
            }
          }) as number
        }

        this.attachmentsObserver.observer = new ResizeObserver(() => {
          console.log(' resize attachment - ')
          this.attachmentsObserver!.calcHeight()
        })

        this.attachmentsObserver.clear = () => {
          const { observer, element, resizeTimer } = this.attachmentsObserver!
          if (observer != null && element != null) {
            observer.disconnect()
            if (resizeTimer != null) {
              cancelAnimationFrame(resizeTimer)
            }
          }
        }
      }

      if (this.attachmentsObserver.ref !== ref || this.attachmentsObserver.element !== ref) {
        this.attachmentsObserver.clear()

        this.attachmentsObserver.element = ref
        this.attachmentsObserver.ref = ref

        if (this.attachmentsObserver.element != null) {
          this.attachmentsObserver.calcHeight()
          this.attachmentsObserver.observer!.observe(this.attachmentsObserver.element, {
            box: 'border-box',
          })
        }
      }
    }
  }

  enableExpandBtn = (enable: boolean) => {
    if (enable !== this.isExpandEnabled) {
      this.isExpandEnabled = enable
      this.setState({
        isExpandEnabled: this.isExpandEnabled,
      })
    }
  }

  setViewNormal = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    ;(window as WindowWithDocUtil).docUtil.mailViewMap[mailInfo.id].checkOption({ mode: 'normal' })
    this.enableExpandBtn(false)
  }

  onClickICal = (e: React.MouseEvent, action: string, resAction?: number) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const resActionValue = resAction != null ? resAction : -1

    console.log('onClick', action, resActionValue)
    let type = -1
    switch (action) {
      case 'accept':
        type = 0
        break
      case 'tentative':
        type = 1
        break
      case 'decline':
        type = 2
        break
      case 'remove-calendar':
        type = 3
        break
      case 'propose-review':
        type = 4
        break
      case 'propose-accept':
        type = 5
        break
      default:
        type = -1
    }

    if (type > -1) {
      IFace.WebToNative.responseMeeting(mailInfo.id, type, resActionValue)
    }
  }

  printEmail = () => {
    const isFold = this.isFold()
    if (isFold === true) {
      this.setFold(false)
      setTimeout(() => {
        this.openPrintWindow()
      }, 100)
      return
    }
    this.openPrintWindow()
  }

  buildPrintDocument = (doc: Document) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const subject = mailInfo.subject || `(${i18n.t('email.desc.noSubject')})`
    const { from } = mailInfo
    const toList = mailInfo.to || []
    const ccList = mailInfo.cc || []
    const bccList = mailInfo.bcc || []
    const { dateTimeFormat } = IFace.getTimeFormats()
    const dateStr = moment(mailInfo.received_time * 1000).format(dateTimeFormat)
    const attachments = mailInfo.body?.attachments || []
    const logoSrc = typeof printLogoSVG === 'string' ? printLogoSVG : ''

    // body DOM은 이미 getSafeHTML()로 sanitize된 콘텐츠이므로, deep clone하여 사용
    const bodyDiv = this.divFrameMap.fullBody || this.divFrameMap.body

    doc.title = subject

    const meta = doc.createElement('meta')
    meta.setAttribute('charset', 'utf-8')
    doc.head.appendChild(meta)

    const style = doc.createElement('style')
    style.textContent = [
      `body {`,
      `  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;`,
      `  font-size: 14px; color: #262626; margin: 0; padding: 20px 0; line-height: 1.5;`,
      `}`,
      `.print-section { padding: 0 20px; }`,
      `.print-logo { margin-bottom: 12px; }`,
      `.print-logo img { height: 18px; }`,
      `.print-subject { font-size: 16px; font-weight: 600; margin: 0 0 12px 0; word-break: break-word; }`,
      `.print-meta { border-collapse: collapse; width: 100%; font-size: 14px; color: #1e1e1e; line-height: 1.5; }`,
      `.print-meta td { padding: 2px 0; vertical-align: top; word-break: break-word; }`,
      `.print-meta .print-meta-label { width: 1%; white-space: nowrap; padding-right: 8px; color: #1e1e1e; font-weight: 400; }`,
      `.print-meta .print-meta-from { font-weight: 700; font-size: 14px; }`,
      `.print-meta .print-meta-date { font-size: 12px; color: #6b7280; font-weight: 400; text-align: right; white-space: nowrap; }`,
      `hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }`,
      `.print-divider { border: none; border-top: 1px solid #E5E7EB; margin: 16px 0; }`,
      `.print-attachments { font-size: 11px; color: #1e1e1e; margin-bottom: 16px; word-break: break-word; border-collapse: collapse; }`,
      `.print-attachments img { vertical-align: middle; }`,
      `.print-attachments b { font-size: 11px; }`,
      `.print-att-size { font-size: 9px; color: #6b7280; }`,
      `.print-ical { margin: 12px 0; padding-top: 10px; padding-bottom: 10px; background: #f9fafb; border: 1px solid #E5E7EB; border-radius: 6px; }`,
      `.print-ical-table { border-collapse: collapse; }`,
      `.print-ical-icon { vertical-align: top; padding-right: 12px; width: 48px; }`,
      `.print-ical-icon-wrapper { position: relative; width: 48px; height: 48px; }`,
      `.print-ical-icon-wrapper img { display: block; }`,
      `.print-ical-date { position: absolute; top: 12px; left: 0; width: 100%; text-align: center; font-size: 13px; font-weight: 700; color: #4c31cb; line-height: 32px; }`,
      `.print-ical-title { font-size: 14px; font-weight: 700; color: #1e1e1e; padding-bottom: 2px; vertical-align: bottom; }`,
      `.print-ical-info { font-size: 13px; color: #6b7280; padding-top: 1px; vertical-align: top; }`,
      `.print-ical-actions { padding-top: 8px; }`,
      `.print-ical-btn + .print-ical-btn { margin-left: 12px; }`,
      `.print-ical-btn { display: inline-block; min-width: 100px; height: 38px; line-height: 36px; padding: 0 16px; text-align: center; border-radius: 6px; border: 1px solid #d1d5db; background: #fff; font-size: 14px; color: #374151; box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }`,
      `.print-ical-btn.accept { color: #007aff; border-color: #007aff; }`,
      `.print-ical-btn.decline { color: #fd4c45; border-color: #fd4c45; }`,
      `.print-ical-btn.tentative { color: #666; border-color: #666; }`,
      `.print-body { line-height: initial; font-size: initial; }`,
      `.print-body *, .print-body ::after, .print-body ::before { box-sizing: initial; }`,
      `.print-body p { margin: initial; }`,
      `.print-body blockquote { margin: revert; }`,
      `.print-body a { text-decoration: underline; }`,
      `.print-body pre { white-space: pre-wrap; word-break: break-word; }`,
      `.print-body table { max-width: 100%; }`,
      `.print-body img, .print-body svg { max-width: 100%; height: auto; }`,
      `@media print {`,
      `  body { padding: 0; margin: 0; }`,
      `  @page { margin: 1.2cm; }`,
      `  .print-body img, .print-body svg {`,
      `    max-width: 100% !important; height: auto !important; page-break-inside: avoid;`,
      `  }`,
      `}`,
    ].join('\n')
    doc.head.appendChild(style)

    const formatRecipient = (r: EmailAddress): string => {
      if (r.name && r.name !== r.email) {
        return `${r.name} <${r.email}>`
      }
      return `<${r.email}>`
    }

    // 로고 섹션
    if (logoSrc) {
      const logoSection = doc.createElement('div')
      logoSection.className = 'print-section'
      const logoDiv = doc.createElement('div')
      logoDiv.className = 'print-logo'
      const logoImg = doc.createElement('img')
      logoImg.src = logoSrc
      logoImg.alt = 'logo'
      logoDiv.appendChild(logoImg)
      logoSection.appendChild(logoDiv)
      doc.body.appendChild(logoSection)
    }

    const hrBefore = doc.createElement('hr')
    hrBefore.className = 'print-divider'
    doc.body.appendChild(hrBefore)

    const subjectSection = doc.createElement('div')
    subjectSection.className = 'print-section'
    const subjectH1 = doc.createElement('h1')
    subjectH1.className = 'print-subject'
    subjectH1.textContent = subject
    subjectSection.appendChild(subjectH1)
    doc.body.appendChild(subjectSection)

    const hrAfter = doc.createElement('hr')
    hrAfter.className = 'print-divider'
    doc.body.appendChild(hrAfter)

    const metaTable = doc.createElement('table')
    metaTable.className = 'print-meta'

    const addMetaRow = (label: string, value: string, className?: string) => {
      const tr = doc.createElement('tr')
      const tdLabel = doc.createElement('td')
      tdLabel.className = 'print-meta-label'
      tdLabel.textContent = `${label}:`
      const tdValue = doc.createElement('td')
      tdValue.textContent = value
      if (className) tdValue.className = className
      tr.appendChild(tdLabel)
      tr.appendChild(tdValue)
      return tr
    }

    // 1행: From + Date (Figma: from bold 14px, date 12px #6b7280 right-aligned)
    const fromRow = doc.createElement('tr')
    const fromValueTd = doc.createElement('td')
    fromValueTd.className = 'print-meta-from'
    fromValueTd.setAttribute('colspan', '2')
    fromValueTd.textContent = formatRecipient(from)
    const dateTd = doc.createElement('td')
    dateTd.className = 'print-meta-date'
    dateTd.textContent = dateStr
    fromRow.appendChild(fromValueTd)
    fromRow.appendChild(dateTd)
    metaTable.appendChild(fromRow)

    // To, Cc, Bcc rows
    if (toList.length > 0) {
      metaTable.appendChild(
        addMetaRow(i18n.t('email.field.to'), toList.map(formatRecipient).join('; ')),
      )
    }
    if (ccList.length > 0) {
      metaTable.appendChild(
        addMetaRow(i18n.t('email.field.cc'), ccList.map(formatRecipient).join('; ')),
      )
    }
    if (bccList.length > 0) {
      metaTable.appendChild(
        addMetaRow(i18n.t('email.field.bcc'), bccList.map(formatRecipient).join('; ')),
      )
    }
    const metaSection = doc.createElement('div')
    metaSection.className = 'print-section'
    metaSection.appendChild(metaTable)
    doc.body.appendChild(metaSection)

    // 캘린더(미팅) 정보
    const { meetingInfo } = this.state
    if (meetingInfo != null) {
      const { dateTimeFormat: dtFormat, timeFormat: tFormat } = IFace.getTimeFormats()
      const startTime = meetingInfo.timezone
        ? moment(meetingInfo.dt_start).tz(meetingInfo.timezone)
        : moment(meetingInfo.dt_start)
      const endTime = meetingInfo.timezone
        ? moment(meetingInfo.dt_end).tz(meetingInfo.timezone)
        : moment(meetingInfo.dt_end)

      const rangeStr = startTime.isSame(endTime, 'day')
        ? `${startTime.format(dtFormat)} - ${endTime.format(tFormat)}`
        : `${startTime.format(dtFormat)} - ${endTime.format(dtFormat)}`

      const startDateStr = startTime.format('MM.DD')

      // 타이틀 생성 (uiExt.getICalElem 로직 참조)
      let icalTitle = ''
      const senderName = getDisplayEmailHTMLStr(meetingInfo.sender)
      const fromName = getDisplayEmailHTMLStr(from)
      const userEmail = getUserEmail()
      const isOwnerMeeting =
        (meetingInfo.type === 0 && meetingInfo.sender.email === userEmail) ||
        (meetingInfo.type !== 0 && meetingInfo.sender.email !== userEmail)

      if (meetingInfo.type === 1) {
        const canceledStr = i18n.t('meeting.title.canceled', { summary: '' })
        icalTitle =
          meetingInfo.title.indexOf(canceledStr) !== 0
            ? i18n.t('meeting.title.canceled', { summary: meetingInfo.title })
            : meetingInfo.title
      } else if (!isOwnerMeeting) {
        let statusTxt = ''
        if (meetingInfo.type === 2) statusTxt = i18n.t('meeting.status.accepted')
        else if (meetingInfo.type === 3) statusTxt = i18n.t('meeting.status.tentative')
        else if (meetingInfo.type === 4) statusTxt = i18n.t('meeting.status.declined')

        icalTitle =
          meetingInfo.sequence != null && meetingInfo.sequence > 0
            ? i18n.t('meeting.title.updated', { organizer: fromName })
            : i18n.t('meeting.title.invited', { organizer: fromName, status: statusTxt })
      } else {
        if (meetingInfo.type === 2)
          icalTitle = i18n.t('meeting.title.accepted', { attendee: fromName })
        else if (meetingInfo.type === 3)
          icalTitle = i18n.t('meeting.title.tentative', { attendee: fromName })
        else if (meetingInfo.type === 4)
          icalTitle = i18n.t('meeting.title.declined', { attendee: fromName })
        else icalTitle = i18n.t('meeting.title.invited', { organizer: senderName, status: '' })
      }

      const hasExtraRow = meetingInfo.recurrence || meetingInfo.location
      const hasActionsRow = meetingInfo.enable_response === true && !isOwnerMeeting
      const iconRowspan = 2 + (hasExtraRow ? 1 : 0) + (hasActionsRow ? 1 : 0)

      // 캘린더 아이콘 + 텍스트 테이블
      const icalSection = doc.createElement('div')
      icalSection.className = 'print-section print-ical'

      const icalTable = doc.createElement('table')
      icalTable.setAttribute('cellspacing', '0')
      icalTable.setAttribute('cellpadding', '0')
      icalTable.setAttribute('border', '0')
      icalTable.className = 'print-ical-table'
      const icalTbody = doc.createElement('tbody')
      const icalRow = doc.createElement('tr')

      // 캘린더 아이콘 셀 (아이콘 위에 날짜 겹침)
      const iconTd = doc.createElement('td')
      iconTd.className = 'print-ical-icon'
      iconTd.setAttribute('rowspan', String(iconRowspan))
      const iconWrapper = doc.createElement('div')
      iconWrapper.className = 'print-ical-icon-wrapper'
      const calImg = doc.createElement('img')
      calImg.src = typeof calender2SVG === 'string' ? calender2SVG : ''
      calImg.setAttribute('width', '48')
      calImg.setAttribute('height', '48')
      iconWrapper.appendChild(calImg)
      const dateOverlay = doc.createElement('div')
      dateOverlay.className = 'print-ical-date'
      dateOverlay.textContent = startDateStr
      iconWrapper.appendChild(dateOverlay)
      iconTd.appendChild(iconWrapper)
      icalRow.appendChild(iconTd)

      // 타이틀 셀
      const titleTd = doc.createElement('td')
      titleTd.className = 'print-ical-title'
      titleTd.innerHTML = icalTitle
      icalRow.appendChild(titleTd)
      icalTbody.appendChild(icalRow)

      // 시간 행
      const timeRow = doc.createElement('tr')
      const timeTd = doc.createElement('td')
      timeTd.className = 'print-ical-info'
      timeTd.textContent = rangeStr
      timeRow.appendChild(timeTd)
      icalTbody.appendChild(timeRow)

      // 반복/장소 행
      const extraParts: string[] = []
      if (meetingInfo.recurrence) extraParts.push(meetingInfo.recurrence)
      if (meetingInfo.location) extraParts.push(meetingInfo.location)
      if (extraParts.length > 0) {
        const extraRow = doc.createElement('tr')
        const extraTd = doc.createElement('td')
        extraTd.className = 'print-ical-info'
        extraTd.textContent = extraParts.join(' | ')
        extraRow.appendChild(extraTd)
        icalTbody.appendChild(extraRow)
      }

      // 미팅 응답 버튼 행 (enableResponse && !isOwnerMeeting)
      if (hasActionsRow) {
        const actionsRow = doc.createElement('tr')
        const actionsTd = doc.createElement('td')
        actionsTd.className = 'print-ical-actions'

        const btnConfigs = [
          { cls: 'accept', label: i18n.t('common.button.accept') },
          { cls: 'decline', label: i18n.t('common.button.decline') },
          { cls: 'tentative', label: i18n.t('common.button.tentative') },
        ]

        btnConfigs.forEach(({ cls, label }) => {
          const btn = doc.createElement('span')
          btn.className = `print-ical-btn ${cls}`
          btn.textContent = label
          actionsTd.appendChild(btn)
        })

        actionsRow.appendChild(actionsTd)
        icalTbody.appendChild(actionsRow)
      }

      icalTable.appendChild(icalTbody)
      icalSection.appendChild(icalTable)
      doc.body.appendChild(icalSection)
    }

    const hr = doc.createElement('hr')
    doc.body.appendChild(hr)

    if (attachments.length > 0) {
      const attTable = doc.createElement('table')
      attTable.className = 'print-attachments'
      attTable.setAttribute('cellspacing', '0')
      attTable.setAttribute('cellpadding', '5')
      attTable.setAttribute('border', '0')
      const tbody = doc.createElement('tbody')

      // 헤더 행: "첨부파일 N개"
      const headerRow = doc.createElement('tr')
      const headerTd = doc.createElement('td')
      headerTd.setAttribute('colspan', '2')
      const headerB = doc.createElement('b')
      headerB.style.paddingLeft = '3px'
      headerB.textContent = i18n.t('email.attachment.files', { total: attachments.length })
      headerTd.appendChild(headerB)
      headerRow.appendChild(headerTd)
      tbody.appendChild(headerRow)

      // 각 첨부파일 행
      attachments.forEach((att: AttachmentObj) => {
        const name = att.name || att.id
        const size = formatBytes(att.size, 1, false) as string
        const ext = name.includes('.') ? name.substring(name.lastIndexOf('.') + 1) : ''
        const iconInfo = getImageInfo(ext)

        const row = doc.createElement('tr')
        const td = doc.createElement('td')
        const innerTable = doc.createElement('table')
        innerTable.setAttribute('cellspacing', '0')
        innerTable.setAttribute('cellpadding', '0')
        const innerTbody = doc.createElement('tbody')
        const innerRow = doc.createElement('tr')

        // 아이콘 셀
        const iconTd = doc.createElement('td')
        const iconImg = doc.createElement('img')
        iconImg.setAttribute('width', '20')
        iconImg.setAttribute('height', '20')
        iconImg.src = iconInfo.path
        iconTd.appendChild(iconImg)
        innerRow.appendChild(iconTd)

        // 간격 셀
        const spacerTd = doc.createElement('td')
        spacerTd.setAttribute('width', '7')
        innerRow.appendChild(spacerTd)

        // 파일명 + 크기 셀
        const nameTd = doc.createElement('td')
        const nameB = doc.createElement('b')
        nameB.textContent = name
        nameTd.appendChild(nameB)
        nameTd.appendChild(doc.createElement('br'))
        const sizeSpan = doc.createElement('span')
        sizeSpan.className = 'print-att-size'
        sizeSpan.textContent = String(size)
        nameTd.appendChild(sizeSpan)
        innerRow.appendChild(nameTd)

        innerTbody.appendChild(innerRow)
        innerTable.appendChild(innerTbody)
        td.appendChild(innerTable)
        row.appendChild(td)
        tbody.appendChild(row)
      })

      attTable.appendChild(tbody)
      const attSection = doc.createElement('div')
      attSection.className = 'print-section'
      attSection.appendChild(attTable)
      doc.body.appendChild(attSection)
    }

    const bodyContainer = doc.createElement('div')
    bodyContainer.className = 'print-body'
    if (bodyDiv != null) {
      const clonedBody = bodyDiv.cloneNode(true) as HTMLElement
      while (clonedBody.firstChild) {
        bodyContainer.appendChild(doc.adoptNode(clonedBody.firstChild))
      }
      // 의사요소 보존 <style> 을 print 문서에서 .print-body 하위로만 적용되도록 @scope 재스코프.
      // (뷰어에서 부여한 @scope([data-email-id] .htmlMailBody) 는 clone 문서에 앵커가 없으므로 교체.)
      applyPseudoStyleScope(bodyContainer, '.print-body')
    }
    const bodySection = doc.createElement('div')
    bodySection.className = 'print-section'
    bodySection.appendChild(bodyContainer)
    doc.body.appendChild(bodySection)
  }

  openPrintWindow = () => {
    const pw = window.open('about:blank', '_blank', 'width=800,height=600')
    if (pw == null) {
      return
    }

    this.printWindow = pw

    this.buildPrintDocument(pw.document)

    const images = pw.document.querySelectorAll('img')
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve()
      return new Promise<void>((resolve) => {
        img.onload = () => resolve()
        img.onerror = () => resolve()
      })
    })

    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000))

    Promise.race([Promise.all(imagePromises), timeout]).then(() => {
      if (pw.closed) return

      pw.focus()
      pw.print()
    })
  }

  onClickBtn = (cmd: string, e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    switch (cmd) {
      case 'reply':
        IFace.WebToNative.reply(mailInfo.id)
        break
      case 'reply-all':
      case 'replyAll':
        IFace.WebToNative.replyAll(mailInfo.id)
        break
      case 'forward':
        IFace.WebToNative.forward(mailInfo.id)
        break
      case 'forwardAsAttachment':
        IFace.WebToNative.forwardAsAttachment(mailInfo.id)
        break
      case 'viewMessageDetails':
        IFace.WebToNative.viewMessageDetails(mailInfo.id)
        break
      case 'download':
        IFace.WebToNative.saveEmail(mailInfo.id)
        break
      case 'inboxImportant':
        IFace.WebToNative.setPriorityInbox(mailInfo.id, 'other')
        break
      case 'inboxOther':
        IFace.WebToNative.setPriorityInbox(mailInfo.id, 'important')
        break
      case 'setRead':
        IFace.WebToNative.setRead(mailInfo.id, !mailInfo.read, true)
        break
      case 'delete':
        IFace.WebToNative.delete(mailInfo.id)
        break
      case 'newMail':
        IFace.WebToNative.newMail(mailInfo.id)
        break
      case 'sendAgain':
        IFace.WebToNative.sendAgain(mailInfo.id)
        break
      case 'print':
        this.printEmail()
        break
      case 'renewSecureAccess':
        IFace.WebToNative.secureSendRenewAccess(mailInfo.id)
        break
      case 'removeSecureAccess':
        IFace.WebToNative.secureSendRemoveAccess(mailInfo.id).then((ret: boolean) => {
          if (ret === true) {
            this.setState({ isSecureMail: false })
          }
        })
        break
      default:
    }

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  setContainerRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      this.containerRef = ref

      if (this.containerObserver == null) {
        this.containerObserver = {} as ContainerObserver

        this.containerObserver.onResize = () => {
          if (this.containerObserver!.resizeTimer != null) {
            cancelAnimationFrame(this.containerObserver!.resizeTimer)
          }
          this.containerObserver!.resizeTimer = requestAnimationFrame(() => {
            if (this.containerObserver!.element != null) {
              const { offsetWidth } = this.containerObserver!.element!

              let isLess400: boolean
              if (offsetWidth > 400) {
                isLess400 = false
              } else {
                isLess400 = true
              }

              if (this.prevLess400 !== isLess400) {
                this.prevLess400 = isLess400
                this.setState({
                  isLess400,
                })
              }
            }

            this.containerObserver!.resizeTimer = undefined
          }) as number
        }

        this.containerObserver.observer = new ResizeObserver(() => {
          this.containerObserver!.onResize()
        })

        this.containerObserver.clear = () => {
          const { observer, element, resizeTimer } = this.containerObserver!
          if (observer != null && element != null) {
            observer.disconnect()
            if (resizeTimer != null) {
              cancelAnimationFrame(resizeTimer)
            }
          }
        }
      }

      if (this.containerObserver.ref !== ref || this.containerObserver.element !== ref) {
        this.containerObserver.clear()

        this.containerObserver.element = ref
        this.containerObserver.ref = ref

        if (this.containerObserver.element != null) {
          this.containerObserver.onResize()
          this.containerObserver.observer!.observe(this.containerObserver.element, {
            box: 'border-box',
          })
        }
      }
    }
  }

  setActionsRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      const refW = ref.offsetWidth
      if (refW > 0 && this.prevRefWidth !== refW) {
        this.setState({
          actionsWidth: refW,
        })
        this.prevRefWidth = refW
      }
    }
  }

  renderEmailItem = (item: EmailAddress, idx: number, array: EmailAddress[]) => {
    const endStr = idx < array.length - 1 ? ';' : ''

    const key = `${item.email}_${idx}`

    const { name, email } = item
    return getDisplayEmailElem({ name, email, endStr, key })
  }

  renderEmailPostfix = (
    isFold: boolean,
    viewCnt: number,
    array: EmailAddress[],
    toggle: () => void,
  ) => {
    const othersCnt = array.length - viewCnt
    if (othersCnt > 0 && array.length > 1) {
      let text = ''
      if (isFold === true) {
        if (othersCnt > 1) {
          text = i18n.t('email.moreMail.others', { count: othersCnt })
        } else {
          text = i18n.t('email.moreMail.other', { count: othersCnt })
        }
      } else {
        text = i18n.t('email.button.showLess')
      }
      return (
        <span
          className={classNames('foldBtn', 'others')}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {}}
          onClick={(e) => {
            toggle()
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {text}
        </span>
      )
    }
    return ''
  }

  getEmailElems = (senders: EmailAddress[]) => {
    const othersCount = senders.length - 2
    return (
      <React.Fragment>
        {senders.slice(0, 2).map(({ name, email }) => (
          <span className="emailSet" key={email} data-email={email} data-name={name}>
            {name && name !== email && <span className="name">{name}</span>}
            <span className="email">&lt;{email}&gt;</span>
            {senders.length > 1 ? '; ' : ' '}
          </span>
        ))}
        {othersCount > 0 && (
          <span className="others" key="others">
            +{othersCount} other{othersCount > 1 && 's'}
          </span>
        )}
      </React.Fragment>
    )
  }

  onFocusChange = ({ focused, visible }: FocusInfo) => {
    // visible === true는 이미 펼쳐져 본문이 렌더된 상태를 뜻한다(setRead/notifyVisible은
    // 앞선 setFold(false) 또는 EmailContainer.select에서 이미 실행됨). 따라서 접혀서
    // 보이지 않는 검색 결과(focused && !visible)일 때만 펼친다 → 불필요한 재펼침/루프 방지.
    if (focused && !visible) {
      this.setFold(false)
    }
  }

  render() {
    const { avatar, infoElem, isDraft, hasExternalImg, isBlockedRes, actionsWidth, isLess400 } =
      this.state

    const { threadItem, hidden, selected, hasTitle, bodySearch } = this.props
    const { mailInfo } = threadItem

    const isFold = this.isFold()
    const clsProps = {
      fold: isFold,
      draft: isDraft,
      loading: this.loading === true,
      hiddenEmail: hidden === true,
      selected,
      scheduled: !!mailInfo.is_scheduled_email,
    }

    let elem: React.ReactNode = null
    if (mailInfo != null) {
      const {
        from,
        body,
        ai_summary: aiShortSummary,
        priority_inbox: priorityInbox,
        is_scheduled_email: isScheduledEmail,
      } = mailInfo
      const sender = from

      let subjectElem: React.ReactNode = null
      if (hasTitle !== false) {
        const title = mailInfo.subject
        const subject =
          title === undefined ? (
            <LoadingOutlined spin />
          ) : (
            title || `(${i18n.t('email.desc.noSubject')})`
          )

        subjectElem = (
          <div className={classNames(styles.subjectWrapper)}>
            <div>
              <span className={classNames(styles.subject)}>{subject}</span>
            </div>
            {isScheduledEmail && (
              <div>
                <Button
                  type="text"
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={() => IFace.WebToNative.reservationMailReschedule(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailReschedule')}
                </Button>
                <Button
                  type="text"
                  variant="text"
                  size="small"
                  color="danger"
                  onClick={() => IFace.WebToNative.reservationMailCancelSend(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailCancelSend')}
                </Button>
                <Button
                  type="text"
                  variant="text"
                  size="small"
                  color="danger"
                  onClick={() => IFace.WebToNative.reservationMailDelete(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailDelete')}
                </Button>
              </div>
            )}
          </div>
        )
      }

      const fromElem = (
        <TextFindableNode
          className="from"
          key="from"
          shallow={!bodySearch}
          onFocusChange={this.onFocusChange}
        >
          {isDraft && <span className="draftPrefix">[{i18n.t('email.desc.draft')}] </span>}

          <span className="emailSet" data-email={sender.email} data-name={sender.name}>
            {sender.name && sender.name !== sender.email && (
              <span className="name">{sender.name}</span>
            )}
            <span className="email">&lt;{sender.email}&gt;</span>
          </span>

          {uiExt.getPriorityInboxElem(this, priorityInbox, styles, isFold)}
        </TextFindableNode>
      )

      const summaryElem = (
        <TextFindableNode className="preview" disabled={!isFold} shallow>
          {aiShortSummary ? aiShortSummary : <Truncate>{mailInfo.summary}</Truncate>}
        </TextFindableNode>
      )

      const RecipientsWrapper: any = !isFold ? TextFindableNode : React.Fragment
      const hasTo = mailInfo.to != null && mailInfo.to.length > 0
      const recipientsElem = (
        <RecipientsWrapper>
          {hasTo && (
            <FoldableFlow
              className="to"
              key="to"
              items={mailInfo.to}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.to')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
              emptyWidth={isLess400 !== true ? actionsWidth || 0 : 0}
              emptyLines={[0]}
            />
          )}
          {mailInfo.cc != null && mailInfo.cc.length > 0 && (
            <FoldableFlow
              className="cc"
              key="cc"
              items={mailInfo.cc}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.cc')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
              emptyWidth={hasTo !== true && isLess400 !== true ? actionsWidth || 0 : 0}
              emptyLines={hasTo !== true ? [0] : undefined}
            />
          )}
          {mailInfo.bcc != null && mailInfo.bcc.length > 0 && (
            <FoldableFlow
              className="bcc"
              key="bcc"
              items={mailInfo.bcc}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.bcc')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
            />
          )}
        </RecipientsWrapper>
      )

      const { dateTimeFormat } = IFace.getTimeFormats()

      const receivedAt = moment(mailInfo.received_time * 1000)
      const receiveDate = receivedAt.format(dateTimeFormat)

      const infoProps = {
        hidden:
          this.loading === true ||
          hasExternalImg === false ||
          isDraft === true ||
          isBlockedRes !== true ||
          infoElem == null,
      }

      const topIconsElem = uiExt.getTopIconsElem(this, isFold, styles)
      const actionElems = uiExt.getActionElems(this, styles)

      const { isReadReceipt, readReceiptStatus } = this.state
      let readReceiptChipElem: React.ReactNode = null
      if (isReadReceipt === true) {
        readReceiptChipElem =
          readReceiptStatus != null ? (
            <ReadReceiptChip
              status={readReceiptStatus}
              onClick={() => this.openReadReceiptDetail()}
              onRefresh={() => this.refreshReadReceipt(threadItem?.mailInfo?.id ?? '')}
            />
          ) : (
            <div className={classNames(styles.readReceiptLoading, 'readReceiptContainer')}>
              <LoadingOutlined spin />
              <span>{i18n.t('readReceipt.chip.loading')}</span>
            </div>
          )
      }

      const detailElem = body != null ? uiExt.getDetailElem(this, styles) : null

      const spinElem =
        (infoElem == null && detailElem == null) || this.loading === true ? (
          <div className={classNames(styles.mailLoading)}>
            <LoadingOutlined spin />
          </div>
        ) : null

      elem = (
        <React.Fragment>
          {subjectElem}
          <div
            ref={(ref) => {
              this.setContainerRef(ref)
            }}
            className={classNames(styles.container, 'emailViewContainer', {
              less400: isLess400,
            })}
            onClick={this.onClickContainer}
          >
            <div className={classNames(styles.mailHeaderWrapper)}>
              <div
                className={classNames(styles.mailHeader, 'mailHeader')}
                onClick={this.onClickHeader}
              >
                <div className={classNames(styles.avatar)} onClick={this.onClickAvatar}>
                  <Avatar src={avatar} />
                </div>
                <div className={classNames(styles.desc)}>
                  <div className={classNames(styles.descInner)}>
                    {fromElem}
                    <div className={classNames(styles.extInfo)}>
                      <div className={classNames(styles.icons)}>{topIconsElem}</div>
                      <div className="receivedAt">{receiveDate}</div>
                    </div>
                  </div>
                  <div className={classNames(styles.descInner)}>
                    <div className={classNames(styles.emailAddressInfo)}>
                      {summaryElem}
                      {recipientsElem}
                    </div>
                    <div
                      ref={(ref) => {
                        this.setActionsRef(ref)
                      }}
                      className={classNames(styles.actions)}
                    >
                      {actionElems}
                    </div>
                  </div>
                </div>
              </div>
              {readReceiptChipElem}
            </div>
            <div
              className={classNames(styles.mailBody, 'mailBody')}
              onClick={(e) => handleSchemaEventClick(e.target)}
            >
              {spinElem}
              <div className={classNames(styles.info, infoProps)}>{infoElem}</div>
              {detailElem}
            </div>
            {this.state.secureMailId != null &&
              !(
                this.state.secureSendExpired != null &&
                new Date(this.state.secureSendExpired) < new Date()
              ) && (
                <div className={styles.secureSendBannerContainer}>
                  {this.state.isSecureMail ? (
                    <ConfidentialBanner
                      secureSendExpired={this.state.secureSendExpired}
                      onRemoveAccess={() => this.onClickBtn('removeSecureAccess')}
                    />
                  ) : (
                    this.state.secureSendExpired != null && (
                      <BlockedBanner onRenewAccess={() => this.onClickBtn('renewSecureAccess')} />
                    )
                  )}
                </div>
              )}
          </div>
        </React.Fragment>
      )
    }

    return (
      <div
        className={classNames(styles.emailView, clsProps)}
        data-email-id={mailInfo.id}
        data-testid="mail-body"
      >
        {elem}
      </div>
    )
  }
}

export default forwardRef((props: EmailViewProps, ref) => {
  const emailViewRef = useRef<EmailView>(null)
  const { bodySearch } = useTextFinderContext()

  useEffect(() => {
    bodySearch && emailViewRef.current?.reqDetails(true)
  }, [bodySearch])

  return <EmailView {...props} bodySearch={bodySearch} ref={composeRefs(emailViewRef, ref)} />
})
